import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import backgroundImage from "./assets/bg-image.png";
import titleRobo from "./assets/en/title.jpg";
import section1 from "./assets/en/section-1.png";
import roboShare from "./assets/en/robo-share.png";
import roboComment from "./assets/en/robo-comment.png";
import theme from "./assets/en/theme.png";
import summary from "./assets/en/summary.png";
import googlePlayBanner from "./assets/en/google-play-banner.png";
import appStoreBanner from "./assets/en/app-store-banner.svg";
import iconLock from "./assets/icon-lock.png";
import iconBackup from "./assets/icon-backup.png";
import iconSearch from "./assets/icon-search.png";
import iconFree from "./assets/icon-free.png";
import "./TopPage.scss";
import { Helmet } from "react-helmet";

import ReactGA from "react-ga4";

const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=net.aidiary";
const APP_STORE_URL = "https://apps.apple.com/jp/app/id6736706558";

function TopPageEn() {
  const navigate = useNavigate();
  const search = useLocation().search;
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const page = query.get("page");
  const tutorial = query.get("tutorial");
  const [visibleSections, setVisibleSections] = useState<Set<number>>(
    new Set(),
  );
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: TopPageEn.name,
      title: TopPageEn.name,
    });
  }, []);

  useEffect(() => {
    // /?page=pp の場合 /ppにリダイレクト
    if (page) {
      console.log("page", page);
      navigate("/" + page);
    }
  }, [navigate, page, query]);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    sectionRefs.current.forEach((section, index) => {
      if (!section) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleSections((prev) => new Set(prev).add(index));
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: "0px 0px -50px 0px",
        },
      );

      observer.observe(section);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  return (
    <div
      className="TopPage-wrap"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundRepeat: "repeat",
        backgroundPosition: "0 0",
        backgroundSize: "auto",
      }}
    >
      <div
        className="App"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundRepeat: "repeat",
          backgroundPosition: "0 0",
          backgroundSize: "auto",
        }}
      >
        <Helmet>
          <title>AI Diary Kirono - Cute & Easy Diary App</title>
          <meta
            name="description"
            content="An AI-powered diary app that lets you write professional-quality diary entries in as little as one minute. Includes shared diaries, automatic backups, secure pattern lock protection, and it's completely free!"
          />
          <meta http-equiv="Content-Script-Type" content="text/javascript" />
          <meta http-equiv="Content-Style-Type" content="text/css" />

          <meta
            property="og:title"
            content="AI Diary Kirono - Cute & Easy Diary App"
          />
          <meta
            property="og:description"
            content="Want to beautifully preserve your precious days, but don’t have the time? AI Diary creates wonderful diary entries with images for you. Packed with features like pattern lock security, shared diaries, and more!"
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://www.ai-diary.jp/" />
          <meta
            property="og:image"
            content="https://www.ai-diary.jp/image/en/thumbnail.png"
          />
          <meta
            property="og:site_name"
            content="AI Diary Kirono - Cute & Easy Diary App"
          />
          <meta property="og:locale" content="en_US" />

          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" />
          <link
            href="https://fonts.googleapis.com/css2?family=Barlow:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=M+PLUS+2:wght@100..900&family=Montserrat:ital,wght@0,100..900;1,100..900&family=Nobile:ital,wght@0,400;0,500;0,700;1,400;1,500;1,700&family=Noto+Sans+JP:wght@100..900&family=Orbitron:wght@400..900&family=Petrona:ital,wght@0,100..900;1,100..900&family=Zen+Maru+Gothic&display=swap"
            rel="stylesheet"
          />
        </Helmet>

        {tutorial !== "true" && (
          <div className="language-area">
            <a href="/">日本語</a>
            <p>/</p>
            <a href="/en">English</a>
            <p>/</p>
            <a href="/zh">中文</a>
          </div>
        )}

        <div
          className="title-area"
          style={{
            backgroundImage: `url(${titleRobo})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <p className="sub-title">
            Preserve your precious days with
            <br />
            beautiful text and images
            <br />
            AI-powered diary app
          </p>
        </div>

        {tutorial !== "true" && (
          <div className="store-image-wrapper">
            <img
              src={googlePlayBanner}
              onClick={() => window.open(GOOGLE_PLAY_URL, "_blank")}
              className="google-playbanner-image"
              alt="Google Play"
            />
            <img
              src={appStoreBanner}
              onClick={() => window.open(APP_STORE_URL, "_blank")}
              className="google-playbanner-image"
              alt="App Store"
            />
          </div>
        )}
        <div
          className={`section scroll-animate ${
            visibleSections.has(0) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[0] = el)}
        >
          <TitleLabel
            label="Features"
            number="①"
            sub="Your everyday life will start to shine!"
            title="AI-powered Diary Creation"
          />
          <img
            src={section1}
            className="section-image trim-vertical"
            alt="AI diary creation"
          />
          <div className="description-wrapper">
            <ul>
              <li>
                Just chat with AI or answer a few simple questions, and AI will
                write your diary for you
              </li>
              <li>
                AI can also generate beautiful images based on your diary
                content
              </li>
              <li>
                With AI voice narration, you can enjoy your diary like listening
                to a story
              </li>
            </ul>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(1) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[1] = el)}
        >
          <TitleLabel
            label="Features"
            number="②"
            sub="Receive comments!"
            title="AI Comments on Your Diary"
          />
          <img
            src={roboComment}
            className="section-image"
            alt="AI diary comments"
          />
          <div className="description-wrapper">
            <ul>
              <li>
                Receive comments from AI characters, helping you stay motivated
                to keep writing
              </li>
              <li>
                When you're feeling down, AI may even give you helpful advice!
              </li>
            </ul>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(2) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[2] = el)}
        >
          <TitleLabel
            label="Features"
            number="③"
            sub="Diary sharing made easy!"
            title="Share Your Diary with Friends"
          />
          <img src={roboShare} className="section-image" alt="Diary sharing" />
          <div className="description-wrapper">
            <ul>
              <li>You can share your diary with friends or make it public</li>
              <li>
                Privacy is fully protected with customizable visibility settings
              </li>
            </ul>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(3) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[3] = el)}
        >
          <TitleLabel
            label="Features"
            number="④"
            sub="Make it truly yours!"
            title="Customize Fonts & Backgrounds"
          />
          <img
            src={theme}
            className="section-image trim-top"
            alt="Customization"
          />
          <div className="description-wrapper">
            <ul>
              <li>
                Customize fonts, text color, size, background color, and
                background images to your liking
              </li>
            </ul>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(4) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[4] = el)}
        >
          <TitleLabel
            label="Features"
            number="⑤"
            sub="Weekly reflection made easy!"
            title="Weekly Summary Feature"
          />
          <div className="section-image-description-row">
            <img src={summary} className="left-image" alt="Weekly summary" />
            <div className="description-wrapper">
              <ul>
                <li>
                  If you write your diary at least three times a week, AI will
                  summarize your entire week for you
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(5) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[5] = el)}
        >
          <TitleLabel
            label="Features"
            number="⑥"
            sub="Protect your precious memories!"
            title="Advanced Security Features"
          />
          <div className="section-image-description-row">
            <img src={iconLock} className="left-image" alt="Security" />
            <div className="description-wrapper">
              <ul>
                <li>Screen lock prevents others from viewing your diary</li>
                <li>
                  AI anonymizes personal names when sharing diaries on social
                  media
                </li>
                <li>All communication with servers is fully encrypted</li>
                <li>No personal information is required</li>
              </ul>
            </div>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(6) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[6] = el)}
        >
          <TitleLabel
            label="Features"
            number="⑦"
            sub="Switch devices worry-free!"
            title="Automatic Backup"
          />
          <div className="section-image-description-row">
            <img src={iconBackup} className="left-image" alt="Backup" />
            <div className="description-wrapper">
              <ul>
                <li>
                  Your diary is safely stored on servers, so you won’t lose it
                  even if you change or lose your phone
                </li>
                <li>
                  You can also easily save your diaries to apps like Google Keep
                  using the bulk share feature
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(7) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[7] = el)}
        >
          <TitleLabel
            label="Features"
            number="⑧"
            sub="Find past entries instantly!"
            title="Powerful Search"
          />
          <div className="section-image-description-row">
            <img src={iconSearch} className="left-image" alt="Search" />
            <div className="description-wrapper">
              <ul>
                <li>Quickly search and find any past diary entry with ease</li>
              </ul>
            </div>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(8) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[8] = el)}
        >
          <TitleLabel
            label="Features"
            number="⑨"
            sub="Completely free to use!"
            title="All Basic Features Are Free"
          />
          <div className="section-image-description-row">
            <img src={iconFree} className="left-image" alt="Free" />
            <div className="description-wrapper">
              <ul>
                <li>
                  Diary creation, AI chat, and image generation are all free
                </li>
                <li>You can write up to five diary entries per day for free</li>
                <li>Premium plans are also available for heavy users</li>
              </ul>
            </div>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(9) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[9] = el)}
        >
          <TitleLabel title="Important Notes" />
          <div className="description-wrapper">
            <ul>
              <li>
                This app uses AI to generate diary content, and the output may
                not always be accurate. Please review before saving or sharing.
              </li>
              <li>
                Please review and agree to the Terms of Service and Privacy
                Policy before using the app.
              </li>
            </ul>
          </div>

          {tutorial !== "true" && (
            <div className="tosppWrapper">
              <Link to="/tos">Terms of Service</Link>・
              <Link to="/privacy">Privacy Policy</Link>・
              <Link to="/deletedata">How to Delete Data</Link>
            </div>
          )}
        </div>

        {tutorial !== "true" && (
          <div
            className={`section scroll-animate ${
              visibleSections.has(10) ? "animate-in" : ""
            }`}
            ref={(el) => (sectionRefs.current[10] = el)}
          >
            <TitleLabel title="Contact" />
            <div className="description-wrapper">
              <a href="mailto:support@ai-daiary.net">support@ai-daiary.net</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

type TitleLabelProps = {
  label?: string;
  number?: string;
  sub?: string;
  title: string;
};
const TitleLabel = (props: TitleLabelProps) => {
  return (
    <div className="section-title-wrapper">
      {props.label && (
        <p className="feature-label">
          {props.label}
          <span>{props.number}</span>
        </p>
      )}
      <div className="section-title-right-wrapper">
        {props.sub && <p className="section-title-sub">{props.sub}</p>}
        <p className="title">{props.title}</p>
      </div>
    </div>
  );
};

export default TopPageEn;
